import {
    Polygon,
    Rectangle,
    Circle,
    OverlayView,
  } from "@react-google-maps/api";
import { COMMON_SHAPE_OPTIONS } from "../constants";
import { COLORS } from "../../../constants/colors";

import { SearchRegion } from "../../../types/neighborhoodSearch";

interface SearchRegionShapeProps extends SearchRegion {
  isSelected?: boolean;
  onClick?: () => void;
  onMouseOver?: (center: { lat: number; lng: number }) => void;
  onMouseOut?: () => void;
  shapeOptions?: Partial<google.maps.PolygonOptions | google.maps.RectangleOptions | google.maps.CircleOptions>;
  label?: string;
  showLabel?: boolean;
}

const SearchRegionShape = ({ type, rectangleBounds, circle, polygonVertices, isSelected, onClick, onMouseOver, onMouseOut, shapeOptions, label, showLabel = false }: SearchRegionShapeProps) => {
    const finalShapeOptions = {
      ...COMMON_SHAPE_OPTIONS,
      ...(isSelected ? {
        fillOpacity: 0.3,
        fillColor: COLORS.PRIMARY,
        strokeColor: COLORS.PRIMARY,
      } : {}),
      ...shapeOptions,
    };

    const handleMouseOver = () => {
      if (!onMouseOver) return;

      let center: { lat: number; lng: number };

      switch (type) {
        case "circle":
          if (!circle) return;
          center = circle.circleCenter;
          break;
        case "rectangle":
          if (!rectangleBounds) return;
          center = {
            lat: (rectangleBounds.north + rectangleBounds.south) / 2,
            lng: (rectangleBounds.east + rectangleBounds.west) / 2
          };
          break;
        case "polygon":
          if (!polygonVertices || polygonVertices.length === 0) return;
          // Calculate centroid of polygon
          const sum = polygonVertices.reduce(
            (acc, vertex) => ({
              lat: acc.lat + vertex.lat,
              lng: acc.lng + vertex.lng
            }),
            { lat: 0, lng: 0 }
          );
          center = {
            lat: sum.lat / polygonVertices.length,
            lng: sum.lng / polygonVertices.length
          };
          break;
        default:
          return;
      }

      onMouseOver(center);
    };

    // Calculate center position for label
    const getLabelPosition = (): { lat: number; lng: number } | null => {
      switch (type) {
        case "circle":
          if (!circle) return null;
          return circle.circleCenter;
        case "rectangle":
          if (!rectangleBounds) return null;
          return {
            lat: rectangleBounds.north,
            lng: rectangleBounds.west
          };
        case "polygon":
          if (!polygonVertices || polygonVertices.length === 0) return null;
          // Calculate centroid of polygon
          const sum = polygonVertices.reduce(
            (acc, vertex) => ({
              lat: acc.lat + vertex.lat,
              lng: acc.lng + vertex.lng
            }),
            { lat: 0, lng: 0 }
          );
          return {
            lat: sum.lat / polygonVertices.length,
            lng: sum.lng / polygonVertices.length
          };
        default:
          return null;
      }
    };

    const labelPosition = getLabelPosition();

    const renderShape = () => {
      switch (type) {
        case "polygon":
          return (
            <Polygon
              onClick={onClick}
              onMouseOver={handleMouseOver}
              onMouseOut={onMouseOut}
              paths={polygonVertices || []}
              options={finalShapeOptions}
            />
          );

        case "rectangle":
          if (!rectangleBounds) return null;
          const bounds = {
            north: rectangleBounds.north,
            south: rectangleBounds.south,
            east: rectangleBounds.east,
            west: rectangleBounds.west,
          };
          return (
            <Rectangle
              onClick={onClick}
              onMouseOver={handleMouseOver}
              onMouseOut={onMouseOut}
              bounds={bounds}
              options={finalShapeOptions}
            />
          );

        case "circle":
          if (!circle) return null;
          return (
            <Circle
              onClick={onClick}
              onMouseOver={handleMouseOver}
              onMouseOut={onMouseOut}
              center={circle.circleCenter}
              radius={circle.circleRadius}
              options={finalShapeOptions}
            />
          );

        default:
          return null;
      }
    };

    return (
      <>
        {renderShape()}
        {showLabel && label && labelPosition && (
          <OverlayView
            position={labelPosition}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div style={{
              padding: '4px 8px',
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#000',
              backgroundColor: 'transparent',
              textAlign: 'center',
              minWidth: '100px',
              ...(type === 'circle' && {
                transform: 'translate(-50%, -50%)'
              }),
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}>
              {label}
            </div>
          </OverlayView>
        )}
      </>
    );
};

export default SearchRegionShape;